import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, Check, Trophy, Info, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState(new Set());
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, unreadRes, readsRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications`),
        axios.get(`${API_URL}/api/notifications/unread-count`),
        // We track read state client-side after fetching
        Promise.resolve({ data: [] })
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(unreadRes.data.unread_count);
    } catch (err) {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markRead = async (notifId) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${notifId}/read`, {});
      setReadIds(prev => new Set([...prev, notifId]));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      // silently fail
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {});
      setReadIds(new Set(notifications.map(n => n.id)));
      setUnreadCount(0);
    } catch (err) {
      // silently fail
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'contest': return <Trophy className="w-4 h-4 text-[#FBBF24]" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-[#FF6B6B]" />;
      default: return <Info className="w-4 h-4 text-[#0EA5E9]" />;
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-[#6B7280]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#FF6B6B] text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h4 className="font-bold text-[#111827] text-sm">Notifications</h4>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#0EA5E9] font-bold hover:underline flex items-center gap-1"
                data-testid="mark-all-read-button"
              >
                <Check className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-[#6B7280] text-sm">
                No notifications yet
              </div>
            ) : (
              notifications.map((notif) => {
                const isRead = readIds.has(notif.id);
                return (
                  <div
                    key={notif.id}
                    className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${!isRead ? 'bg-blue-50/50' : ''}`}
                    onClick={() => !isRead && markRead(notif.id)}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className="flex gap-3">
                      <div className="mt-0.5">{getIcon(notif.type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#111827] truncate">{notif.title}</p>
                        <p className="text-xs text-[#6B7280] mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-xs text-[#9CA3AF] mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      {!isRead && <div className="w-2 h-2 rounded-full bg-[#0EA5E9] mt-2 shrink-0"></div>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
